import React, { createContext, useContext, useState, useCallback } from "react";
import { getApiBaseUrl } from "../config";
import { useAuth } from "../context/ApiKeyContext";

const API_BASE_URL = getApiBaseUrl();

const WizardContext = createContext(null);

export function WizardProvider({ children }) {
  const { hasRole } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [activeStepIndex, setActiveStepIndex] = useState(0);

  const fetchSessions = useCallback(async () => {
    const token = localStorage.getItem("ikaro_auth_token");
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/wizard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setSessions(data.sessions || []);
    } catch {
      // wizard não bloqueia a aplicação
    }
  }, []);

  const startSession = useCallback((session) => {
    if (!session || !session.steps || session.steps.length === 0) return;
    const sorted = [...session.steps]
      .sort((a, b) => a.step_order - b.step_order)
      .filter((step) => {
        if (!step.required_role) return true;
        const [service, code] = step.required_role.split(":");
        return hasRole(service, code);
      });
    if (sorted.length === 0) return;
    setActiveSession({ ...session, steps: sorted });
    setActiveStepIndex(0);
  }, [hasRole]);

  const hasSeen = useCallback(
    (sessionId) => {
      const s = sessions.find((s) => s.id === sessionId);
      return s ? s.completed : false;
    },
    [sessions]
  );

  const completeSession = useCallback(async (sessionId) => {
    const token = localStorage.getItem("ikaro_auth_token");
    if (!token) return;
    try {
      await fetch(`${API_BASE_URL}/wizard/sessions/${sessionId}/complete`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      setSessions((prev) =>
        prev.map((s) => (s.id === sessionId ? { ...s, completed: true } : s))
      );
    } catch {}
  }, []);

  const resetSession = useCallback(async (sessionId) => {
    const token = localStorage.getItem("ikaro_auth_token");
    if (!token) return;
    try {
      await fetch(`${API_BASE_URL}/wizard/sessions/${sessionId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      setSessions((prev) =>
        prev.map((s) => (s.id === sessionId ? { ...s, completed: false } : s))
      );
    } catch {}
  }, []);

  const closeSession = useCallback(() => {
    setActiveSession(null);
    setActiveStepIndex(0);
  }, []);

  const nextStep = useCallback(() => {
    if (!activeSession) return;
    if (activeStepIndex < activeSession.steps.length - 1) {
      setActiveStepIndex((i) => i + 1);
    } else {
      completeSession(activeSession.id);
      closeSession();
    }
  }, [activeSession, activeStepIndex, completeSession, closeSession]);

  const skipSession = useCallback(() => {
    if (!activeSession) return;
    completeSession(activeSession.id);
    closeSession();
  }, [activeSession, completeSession, closeSession]);

  return (
    <WizardContext.Provider
      value={{
        sessions,
        activeSession,
        activeStepIndex,
        startSession,
        hasSeen,
        resetSession,
        nextStep,
        skipSession,
        closeSession,
        refetch: fetchSessions,
      }}
    >
      {children}
    </WizardContext.Provider>
  );
}

export function useWizard() {
  const ctx = useContext(WizardContext);
  if (!ctx) throw new Error("useWizard must be used inside WizardProvider");
  return ctx;
}
